import './styles/branding.css';
import Unsubscribe from './pages/Unsubscribe';
import AdminNewsletters from './pages/admin/AdminNewsletters';
import GuidedHelp from './components/GuidedHelp';
import Discover from './pages/Discover';
import AdminDiscoverList from './pages/admin/AdminDiscoverList';
import AdminDiscover from './pages/admin/AdminDiscover';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import CategoryPage from './pages/CategoryPage';
import Reader from './pages/Reader';
import Quiz from './pages/Quiz';
import Listen from './pages/Listen';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyPending from './pages/VerifyPending';
import VerifyEmail from './pages/VerifyEmail';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import About from './pages/About';
import Legal from './pages/Legal';
import Subscribe from './pages/Subscribe';
import Settings from './pages/Settings';
import SelectProfile from './pages/SelectProfile';
import SchoolHome from './pages/SchoolHome';
import TeacherHome from './pages/TeacherHome';
import SchoolLogin from './pages/SchoolLogin';
import SchoolPortal from './pages/SchoolPortal';
import SchoolJoin from './pages/SchoolJoin';
import PremiumRoute from './components/PremiumRoute';
import PremiumGateModal from './components/PremiumGateModal';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import axios from 'axios';
import AdminLayout from './pages/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBooksList from './pages/admin/AdminBooksList';
import AdminBookEdit from './pages/admin/AdminBookEdit';
import AdminUsers from './pages/admin/AdminUsers';
import AdminMessages from './pages/admin/AdminMessages';
import AdminRatings from './pages/admin/AdminRatings';
import AdminBilling from './pages/admin/AdminBilling';
import AdminCategories from './pages/admin/AdminCategories';
import AdminGenres from './pages/admin/AdminGenres';
import AdminSocialLinks from './pages/admin/AdminSocialLinks';
import AdminSchools from './pages/admin/AdminSchools';
import { ThemeProvider } from './contexts/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SiteGate from './components/SiteGate';
import { ensureProfileAxiosInterceptor, getActiveProfileId } from './utils/profile';

ensureProfileAxiosInterceptor();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Routes where the navbar/footer shell should be hidden — full-bleed
 * pages like the profile picker (Netflix-style) and the admin studio
 * (which has its own sidebar layout).
 */
const FULLSCREEN_RE = /^\/(login|register|profiles\/select|admin|subscribe|settings|school|teacher|school-login|discover|quiz\/)/;
// (Login + register share Auth.js which already has its own background
// and floating back link, so the navbar/footer would just be chrome.)
function isFullscreen(pathname /* , search */) {
  return FULLSCREEN_RE.test(pathname);
}
function NavChrome() {
  const { pathname, search } = useLocation();
  if (isFullscreen(pathname, search)) return null;
  return <Navbar />;
}
function FooterChrome() {
  const { pathname, search } = useLocation();
  if (isFullscreen(pathname, search)) return null;
  return <Footer />;
}

/**
 * Staff (teacher / school admin) and student accounts don't have the
 * parent-account multi-profile + billing surface. Bounce them to their
 * own dashboard if they land on /profile, /profiles/select, or /settings
 * — whether by direct URL, stale link, or a stray post-login redirect.
 */
/**
 * Staff (teacher / school admin) never go through /profiles/select, so
 * their `adeeb-active-profile` localStorage stays empty — and the
 * reader endpoints (progress / favorites / playlist) require the
 * X-Profile-Id header. Fetch their default profile (the migration
 * makes one for every User on boot) and set it as active so My Shelf,
 * Read, and Listen pages all work for them.
 */
function StaffActiveProfileBootstrap() {
  useEffect(() => {
    let cancelled = false;
    const ensureActiveProfile = async () => {
      const token = localStorage.getItem('adeeb-token');
      if (!token) return;
      let user = null;
      try { user = JSON.parse(localStorage.getItem('adeeb-user') || 'null'); }
      catch { /* ignore */ }
      const role = user?.accountType;
      if (role !== 'teacher' && role !== 'school') return;
      try {
        const axios = (await import('axios')).default;
        const { setActiveProfile } = await import('./utils/profile');
        const headers = { Authorization: `Bearer ${token}` };
        // Always fetch the current user's personal profiles — never skip
        // on the basis of `getActiveProfileId()` alone, because that id
        // could be a stale one from a prior parent session in the same
        // browser. If we sent it the API returns PROFILE_FORBIDDEN and
        // every reader-scoped tab (progress, favourites, playlist,
        // quizzes, rewards) silently dies. Validate-or-replace instead.
        //
        // /api/profiles filters to schoolId:null personal profiles, so
        // the student profiles the teacher manages aren't in this list.
        const r = await axios.get('/api/profiles', { headers });
        const all = Array.isArray(r?.data?.profiles) ? r.data.profiles : [];
        const personalProfiles = all.filter((p) => !p.schoolId);

        // If a cached active profile exists and matches one we just
        // fetched, keep it; otherwise drop it and pick (or create) one
        // that actually belongs to this staff user.
        const cachedId = getActiveProfileId();
        let personal = cachedId && personalProfiles.find((p) => String(p._id) === String(cachedId));

        if (!personal) {
          personal = personalProfiles[0] || null;
          if (!personal) {
            // No personal profile yet — create one. Reader endpoints
            // need an active profile; without this the teacher's My
            // Shelf, favourites, playlist, progress all silently no-op.
            const created = await axios.post('/api/profiles',
              { name: user?.name || 'My shelf', avatar: user?.avatar || '' },
              { headers });
            personal = created?.data?.profile || created?.data;
          }
        }
        // setActiveProfile overwrites, so replacing a stale id is a
        // single emit. Skip if it already matches.
        if (personal && !cancelled && String(personal._id) !== String(cachedId)) {
          setActiveProfile(personal);
        }
      } catch { /* swallow — non-fatal */ }
    };
    ensureActiveProfile();
    window.addEventListener('adeeb-auth-change', ensureActiveProfile);
    return () => {
      cancelled = true;
      window.removeEventListener('adeeb-auth-change', ensureActiveProfile);
    };
  }, []);
  return null;
}

function StaffRedirectGuard() {
  const location = useLocation();
  useEffect(() => {
    if (!localStorage.getItem('adeeb-token')) return;
    let user = null;
    try { user = JSON.parse(localStorage.getItem('adeeb-user') || 'null'); } catch { /* ignore */ }
    const role = user?.accountType;
    if (role !== 'teacher' && role !== 'school' && role !== 'student') return;
    // Honour the session mode — when a user signed in via the parent
    // form (parent mode), they explicitly want the parent flow even
    // if their User row has a staff accountType (same email used for
    // both flows). Only enforce the lock for staff-mode sessions and
    // for students (who only have the kid-login flow).
    const sessionMode = localStorage.getItem('adeeb-session-mode');
    if (sessionMode === 'parent' && role !== 'student') return;
    // Block parent-only screens otherwise:
    //   /profiles/select — multi-profile picker (parent flow)
    //   /settings        — parent billing + email
    const blockedRe = /^\/(profiles\/select|settings)/;
    if (!blockedRe.test(location.pathname)) return;
    const dest = role === 'student' ? '/' : '/teacher';
    if (location.pathname !== dest) window.location.replace(dest);
  }, [location.pathname]);
  return null;
}

/**
 * If a reader-scoped API call fires without an active profile, the axios
 * interceptor dispatches `adeeb-profile-needed`. Bounce the user to the
 * picker — but only when they're authenticated (otherwise let the auth
 * gate take over) and not already on the picker / login flows.
 */
function ProfileGuard() {
  const location = useLocation();
  // Read the cached user once per render — used to skip the picker
  // redirect for staff (who have no reader profile system).
  const accountType = (() => {
    try { return JSON.parse(localStorage.getItem('adeeb-user') || 'null')?.accountType || null; }
    catch { return null; }
  })();
  const isStaff = accountType === 'teacher' || accountType === 'school';

  useEffect(() => {
    const onNeeded = () => {
      const onPicker = location.pathname.startsWith('/profiles');
      // /subscribe is an account-level page: it must not bounce a signed-in visitor to the picker.
      const onAuth   = /^\/(login|register|forgot-password|reset-password|verify|subscribe|auth\/callback|school-login)/.test(location.pathname);
      if (onPicker || onAuth) return;
      if (!localStorage.getItem('adeeb-token')) return;
      // Staff (teacher / school admin) shouldn't be sent to the
      // parent profile picker — they have no reader profile. Send
      // them home to their dashboard instead.
      if (isStaff) {
        if (location.pathname !== '/teacher') window.location.replace('/teacher');
        return;
      }
      window.location.replace('/profiles/select');
    };
    window.addEventListener('adeeb-profile-needed', onNeeded);
    return () => window.removeEventListener('adeeb-profile-needed', onNeeded);
  }, [location.pathname, isStaff]);
  // Also: when a logged-in user lands somewhere that *needs* a profile but
  // hasn't picked one yet (e.g. fresh session, deep-linked into /profile),
  // forward them. Skipped for staff for the same reason as above.
  useEffect(() => {
    const needs = /^\/profile(\/|$)/.test(location.pathname);
    if (!needs) return;
    if (!localStorage.getItem('adeeb-token')) return;
    if (isStaff) return;
    if (getActiveProfileId()) return;
    window.location.replace(`/profiles/select?next=${encodeURIComponent(location.pathname)}`);
  }, [location.pathname, isStaff]);
  return null;
}

/**
 * After Stripe Checkout the user lands on
 *   <something>?subscription=success&session_id=…
 * but the Stripe webhook that flips `user.subscription.status` to
 * `active` is async — often 1-5s behind. The premium gate would
 * otherwise see the still-inactive status and immediately re-block the
 * user. Poll /api/billing/status until active (or give up after ~18s),
 * then refresh the context + close any open paywall + strip the query
 * params so a reload won't re-trigger.
 */
function SubscriptionReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const sub = useSubscription();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('subscription') !== 'success') return;

    let cancelled = false;
    let attempts = 0;
    const MAX = 12;
    const tick = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const token = localStorage.getItem('adeeb-token');
        const { data } = await axios.get('/api/billing/status', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (data && data.active) {
          await sub.refresh();
          sub.closeGate();
          const next = new URLSearchParams(location.search);
          next.delete('subscription');
          next.delete('session_id');
          const qs = next.toString();
          navigate(`${location.pathname}${qs ? '?' + qs : ''}`, { replace: true });
          return;
        }
      } catch { /* ignore — keep polling */ }
      if (attempts < MAX && !cancelled) {
        setTimeout(tick, 1500);
      } else if (!cancelled) {
        // Final refresh attempt; webhook might still arrive later.
        sub.refresh();
      }
    };
    tick();
    return () => { cancelled = true; };
    // intentionally only react to the search string changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  return null;
}

function App() {
  return (
    <SiteGate>
    <ThemeProvider>
      <SubscriptionProvider>
      <Router>
        <ScrollToTop />
        <SubscriptionReturnHandler />
        <StaffRedirectGuard />
        <StaffActiveProfileBootstrap />
        <ProfileGuard />
        <NavChrome />
        <PremiumGateModal />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/category/:tag" element={<CategoryPage />} />
          {/* /read/:id is intentionally NOT wrapped in PremiumRoute —
             guests get a preview (cover + first 3 content pages) via
             /api/books/:id/preview, and Reader opens the paywall gate
             once they reach the last preview page. Listen + Quiz stay
             gated because there's no preview path for those yet. */}
          <Route path="/read/:id" element={<Reader />} />
          <Route path="/quiz/:id" element={<PremiumRoute><Quiz /></PremiumRoute>} />
          <Route path="/listen/:id" element={<PremiumRoute><Listen /></PremiumRoute>} />
          <Route path="/login" element={<Auth />} />
          <Route path="/register" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-pending" element={<VerifyPending />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/newsletter/unsubscribe" element={<Unsubscribe />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profiles/select" element={<SelectProfile />} />
          <Route path="/school" element={<Navigate to="/teacher" replace />} />
          <Route path="/teacher" element={<TeacherHome />} />
          <Route path="/school-login" element={<SchoolLogin />} />
          <Route path="/school-portal" element={<SchoolPortal />} />
          <Route path="/school-join" element={<SchoolJoin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="newsletters" element={<AdminNewsletters />} />
            <Route path="discover" element={<AdminDiscoverList />} />
            <Route path="discover/:quizId" element={<AdminDiscover />} />
            <Route path="books" element={<AdminBooksList />} />
            <Route path="books/:id" element={<AdminBookEdit />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="genres" element={<AdminGenres />} />
            <Route path="social-links" element={<AdminSocialLinks />} />
            <Route path="schools" element={<AdminSchools />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="ratings" element={<AdminRatings />} />
            <Route path="billing" element={<AdminBilling />} />
          </Route>
        </Routes>
        <GuidedHelp />
        <FooterChrome />
      </Router>
      </SubscriptionProvider>
    </ThemeProvider>
    </SiteGate>
  );
}

export default App;
